import { useTheme } from "@/context/ThemeContext";
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { getLessonContext } from "@/data/curriculum";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const { setTheme } = useTheme();
  const [user, setUser] = useState(null); // null = checking, false = anon, object = user
  const [progress, setProgress] = useState(null);
  const [purchases, setPurchases] = useState([]); // array of "instrumentId:courseId"

  const refreshProgress = useCallback(async () => {
    try {
      const { data } = await api.get("/progress");
      setProgress(data);
      return data;
    } catch (e) {
      return null;
    }
  }, []);

  const refreshPurchases = useCallback(async () => {
    try {
      const { data } = await api.get("/purchases");
      setPurchases(data.course_keys || []);
      return data.course_keys || [];
    } catch (e) {
      return [];
    }
  }, []);

  const loadSession = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      if (data && data.authenticated) {
        await Promise.all([refreshProgress(), refreshPurchases()]);
        setUser(data);
      } else {
        if (data?.reason === "other_device") toast.info("You've been signed out on this device because your account was signed in on another device. Your progress is saved to your account — log in again to carry on here.", { id: "other-device-signout", duration: 10000 });
        setUser(false);
      }
    } catch (e) {
      setUser(false);
    }
  }, [refreshProgress, refreshPurchases]);

  const reloadUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      if (data && data.authenticated) {
        setUser(data);
        return data;
      }
      return null;
    } catch (e) {
      return null;
    }
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      await Promise.all([refreshProgress(), refreshPurchases()]);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, status: e.response?.status, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (name, email, password, role = "user") => {
    try {
      const { data } = await api.post("/auth/register", { name, email, password, role });
      setUser(data);
      await Promise.all([refreshProgress(), refreshPurchases()]);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {}
    setUser(false);
    setProgress(null);
    setPurchases([]);
    setTheme("dark");
  };

  const completeLesson = async (lessonId) => {
    try {
      const { data } = await api.post("/progress/complete-lesson", { lesson_id: lessonId });
      setProgress(data);
      const ctx = getLessonContext(Number(lessonId));
      const done = new Set(data.completed_lessons || []);
      if (ctx && ctx.course.lessons.every((l) => done.has(l.id))) {
        await api.post("/certificates/claim", { course_key: `${ctx.instrument.id}:${ctx.course.id}`, lesson_ids: ctx.course.lessons.map((l) => l.id) });
      }
    } catch (e) {}
  };

  const touchLesson = useCallback(async (lessonId) => {
    try {
      const { data } = await api.post("/progress/touch", { lesson_id: lessonId });
      setProgress(data);
    } catch (e) {}
  }, []);

  const saveQuiz = async (quizId, score, total) => {
    try {
      const { data } = await api.post("/progress/quiz", { quiz_id: quizId, score, total });
      setProgress(data);
    } catch (e) {}
  };

  return (
    <AuthContext.Provider
      value={{ user, progress, purchases, login, register, logout, completeLesson, touchLesson, saveQuiz, refreshProgress, refreshPurchases, reloadUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
